import type { DayHeatmap, WeekHeatmap } from '@busy/api';
import { useUI } from '../../store/ui';
import type { ForecastDay } from '../../lib/weather';
import { toLocalISODate } from '../../lib/useWeatherByDate';
import type { ForecastDayPlan } from '../../lib/useCapacityForecast';
import { WeatherChip } from './WeatherChip';

/** A day row in the unified heatmap. Past + today rows are vanilla
 *  DayHeatmap entries from useCapacityUtilization. Future rows are
 *  synthesized from a ForecastDayPlan and tagged `is_forecast=true`
 *  so the renderer can apply the desaturated treatment + attach the
 *  forecast detail to the tap popover. */
export type HeatmapRow = DayHeatmap & {
  is_forecast?: boolean;
  /** Attached when is_forecast is true. Drives the tap-to-expand
   *  forecast detail (expected cuts band, revenue range, walk-in
   *  driven flag, confidence). */
  forecast?: ForecastDayPlan;
};

// 7-day × 16-hour activity heatmap. One row per day (Mon–Sun), each row
// is 16 colored cells representing 6am–10pm. Each cell's color reflects
// utilization of the barber's hourly target — a 5-bucket gradient from
// deep red (low) to deep green (target met or exceeded). Empty hours
// (no cuts) stay flat gray.
//
//   pct = 0       → gray (no cuts that hour)
//   pct  1–19%    → red    (way under target)
//   pct 20–39%    → orange
//   pct 40–59%    → amber  (middle of the gradient)
//   pct 60–79%    → lime   (close)
//   pct 80%+      → green  (target met)
//
// The user controls what "target" means via Settings → Profile →
// Hourly target. 100% means a fully utilized hour is 60 minutes of
// cuts; 80% means 48 minutes; 70% means 42 minutes. Same chart, but
// the barber's own definition of "good" sets the bar.

/** Map a utilization percentage to a Tailwind background class.
 *  Buckets at 0 / 20 / 40 / 60 / 80, matching the legend gradient. */
export function hourCellClass(pct: number): string {
  if (pct <= 0) return 'bg-bg-alt';
  if (pct < 20) return 'bg-red-500';
  if (pct < 40) return 'bg-orange-500';
  if (pct < 60) return 'bg-amber-400';
  if (pct < 80) return 'bg-lime-500';
  return 'bg-emerald-500';
}

export function CapacityHeatmap({
  data,
  todayLabel,
  weatherByDate,
  onRowTap,
}: {
  data: WeekHeatmap & { days: HeatmapRow[] };
  todayLabel: string;
  /** Optional date→weather lookup. When provided, each row's day-label
   *  column shows a small high-temp + condition chip — barbers correlate
   *  "rainy Tuesdays = slow days" without leaving the dashboard. Pass
   *  `{}` (or omit) to render the heatmap with no weather column; the
   *  layout adapts (label column width shrinks). */
  weatherByDate?: Record<string, ForecastDay>;
  /** Tap handler for a row. Called with the row + the dom rect of the
   *  tapped element so the popover can position itself relative to
   *  the row. When omitted, rows render as non-interactive divs. */
  onRowTap?: (row: HeatmapRow, anchor: DOMRect) => void;
}) {
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';

  // Hour labels show every 3 hours so the row stays uncluttered on phones.
  // 6 / 9 / 12 / 3 / 6 / 9 maps to indexes 0, 3, 6, 9, 12, 15.
  const totalHours = data.hour_end - data.hour_start;
  const labelEvery = 3;
  const hourLabels: { idx: number; label: string }[] = [];
  for (let i = 0; i < totalHours; i += labelEvery) {
    const h = data.hour_start + i;
    const display = h === 12 ? '12' : h > 12 ? String(h - 12) : String(h);
    hourLabels.push({ idx: i, label: display });
  }

  // Label-column width adapts to whether we have weather to show. With
  // chips we need ~3.5rem to fit "78°☀"; without, the original 2.75rem
  // is enough for the "Mon / 5/13" stack. Hour-scale left padding above
  // the cell grid mirrors this so the column edges line up.
  const hasWeather = !!weatherByDate && Object.keys(weatherByDate).length > 0;
  const labelColCls = hasWeather ? 'w-14' : 'w-11';
  const hourScalePadCls = hasWeather ? 'pl-[4rem]' : 'pl-[3.25rem]';

  return (
    <div className="space-y-1.5">
      {/* Hour scale — same column width as the cell grid below so labels
          line up with their bucket. Left pad matches the day-label
          column width (w-11) so the hour labels start at the same x
          where the heatmap cells begin. */}
      <div
        className={`grid items-end ${hourScalePadCls} pr-1 text-[9px] font-bold text-ink-500 tabular-nums`}
        style={{ gridTemplateColumns: `repeat(${totalHours}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: totalHours }).map((_, i) => {
          const m = hourLabels.find((l) => l.idx === i);
          return <span key={i}>{m?.label ?? ''}</span>;
        })}
      </div>

      {/* Day rows — label column shows day-of-week stacked over the
          calendar date so the barber can tell two weeks apart when
          paging through history. */}
      <div className="space-y-1">
        {(data.days as HeatmapRow[]).map((d) => {
          const date = new Date(d.day_ms);
          const dayShort = date.toLocaleDateString(dateLocale, { weekday: 'short' });
          const dateShort = date.toLocaleDateString(dateLocale, {
            month: 'numeric',
            day: 'numeric',
          });
          const ariaDate = date.toLocaleDateString(dateLocale, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
          // Day classification — past/today read with full saturation;
          // forecast rows render with the same color gradient but
          // desaturated so the eye can tell "predicted" from "actual"
          // at a glance. Forecast rows with an empty hours[] (no
          // hourly pattern available for that DoW) fall back to the
          // classic diagonal-stripe "not yet" pattern so we don't
          // fabricate cells we can't back with data.
          const isForecast = !!d.is_forecast;
          const hasHourlyForecast = isForecast && d.hours.length === 16;
          const stripedRow = isForecast && !hasHourlyForecast;
          const weather = weatherByDate?.[toLocalISODate(d.day_ms)] ?? null;
          const tappable = !!onRowTap;
          const rowAria = isForecast
            ? `${ariaDate} forecast`
            : `${ariaDate} activity`;

          // Pull the row contents into a variable so the same JSX can
          // render inside either a <button> (tappable) or a <div>
          // (read-only) wrapper without duplicating markup.
          const rowInner = (
            <>
              <div
                className={`${labelColCls} shrink-0 leading-tight ${
                  d.is_today
                    ? 'text-navy-900 dark:text-gold-400'
                    : 'text-ink-700'
                }`}
                title={d.is_today ? `${todayLabel} · ${ariaDate}` : ariaDate}
              >
                <div className="text-[10px] font-bold tabular-nums">{dayShort}</div>
                <div
                  className={`text-[9px] tabular-nums ${
                    d.is_today ? 'text-navy-900/70 dark:text-gold-400/80' : 'text-ink-500'
                  }`}
                >
                  {dateShort}
                </div>
                {weather && (
                  <div className="mt-0.5">
                    <WeatherChip day={weather} size="sm" />
                  </div>
                )}
              </div>
              <div
                className={`flex-1 grid gap-px h-6 rounded overflow-hidden ${
                  d.is_today ? 'ring-1 ring-navy-900/40 dark:ring-gold-500/40' : ''
                } ${isForecast ? 'opacity-60' : ''}`}
                style={{ gridTemplateColumns: `repeat(${totalHours}, minmax(0, 1fr))` }}
                role="row"
                aria-label={rowAria}
              >
                {Array.from({ length: totalHours }).map((_, i) => {
                  const pct = d.hours[i] ?? 0;
                  return (
                    <div
                      key={i}
                      className={stripedRow ? 'bg-bg-alt' : hourCellClass(pct)}
                      role="cell"
                      aria-label={
                        stripedRow
                          ? `${data.hour_start + i}:00 not yet`
                          : isForecast
                            ? `${data.hour_start + i}:00 ~${Math.round(pct)}% expected`
                            : `${data.hour_start + i}:00 ${pct}% utilized`
                      }
                      style={
                        stripedRow
                          ? {
                              backgroundImage:
                                'repeating-linear-gradient(135deg, rgba(120,120,120,0.18) 0 3px, transparent 3px 7px)',
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </>
          );

          return tappable ? (
            <button
              key={d.day_ms}
              type="button"
              onClick={(e) => onRowTap?.(d, e.currentTarget.getBoundingClientRect())}
              className="flex items-center gap-2 w-full text-left rounded-md transition-colors hover:bg-bg-alt/40 focus:bg-bg-alt/50 focus:outline-none"
            >
              {rowInner}
            </button>
          ) : (
            <div key={d.day_ms} className="flex items-center gap-2">
              {rowInner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Single-bar gradient legend that mirrors hourCellClass's 5 buckets.
 *  Replaces the chip-per-state legend now that we're shading by
 *  percentage. Labels at the endpoints (0% / 100%) keep the meaning
 *  obvious without bombarding the user with 5 individual chips. */
export function HeatmapLegend({
  lowLabel,
  highLabel,
}: {
  lowLabel: string;
  highLabel: string;
}) {
  return (
    <div className="flex items-center gap-2 text-[10px] text-ink-500">
      <span className="tabular-nums">{lowLabel}</span>
      <div
        className="flex-1 h-2 rounded-full overflow-hidden flex"
        aria-hidden
      >
        <span className="flex-1 bg-red-500" />
        <span className="flex-1 bg-orange-500" />
        <span className="flex-1 bg-amber-400" />
        <span className="flex-1 bg-lime-500" />
        <span className="flex-1 bg-emerald-500" />
      </div>
      <span className="tabular-nums">{highLabel}</span>
    </div>
  );
}
